import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Zap } from 'lucide-react'; // CheckCircle2 used in pricing columns
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { trackEvent, EVENTS } from '../utils/analytics';

export default function Pricing() {
  const navigate = useNavigate();
  const [proMonthlyCredits, setProMonthlyCredits] = useState(300);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Comes from plan_configs (single source of truth, see
    // utils/planTiers.js) — 300 above is only the fallback before this
    // resolves, or if it fails.
    fetch('/api/subscription/pricing')
      .then((r) => r.json())
      .then((data) => {
        if (Number.isFinite(data?.proMonthlyCredits)) setProMonthlyCredits(data.proMonthlyCredits);
      })
      .catch(() => {});
  }, []);

  const handleGetStarted = () => {
    trackEvent(EVENTS.CTA_CLICKED, {
      cta_id: 'pricing_get_started',
      section: 'pricing'
    });
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-brand-900 flex flex-col">
      <Header />

      <main className="flex-grow">
        {/* Hero */}
        <section className="relative overflow-hidden bg-brand-900 text-brand-50">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:80px_80px]"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-900/50 to-brand-900"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10 pt-40 pb-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <h1 className="text-5xl md:text-8xl lg:text-[100px] font-medium text-brand-50 mb-8 leading-[0.95]">
                Product authority.<br />Without the product team.
              </h1>

              <p className="text-xl text-brand-400 leading-relaxed max-w-2xl mx-auto mb-12 font-light">
                Pabs gives founders and AI-enabled teams the product direction, Design Authority,
                and continuous review needed to build with confidence.
              </p>

              <button
                onClick={handleGetStarted}
                className="h-16 px-10 bg-brand-50 text-brand-900 hover:bg-brand-200 text-lg rounded-full font-bold shadow-xl transition-all duration-300"
              >
                Get started for free
              </button>
            </motion.div>
          </div>
          </div>
        </section>

        {/* Three column pricing */}
        <section className="py-20 px-4 border-t border-brand-800">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* Free - visual hero */}
              <div className="md:col-span-1 bg-brand-50 rounded-3xl p-8 flex flex-col relative">
                <div className="absolute top-4 right-4 px-3 py-1 bg-brand-900 text-brand-50 text-xs font-bold rounded-full uppercase tracking-wide">
                  Start here
                </div>
                <p className="text-xs font-bold text-brand-700 uppercase tracking-widest mb-4">Free</p>
                <div className="mb-1">
                  <span className="text-5xl font-bold text-brand-900">$0</span>
                  <span className="text-brand-600 text-sm ml-1">/month</span>
                </div>
                <p className="text-brand-600 text-sm mb-8">This isn't a trial. It's the real thing.</p>

                <ul className="space-y-3 flex-grow">
                  {[
                    "Define the product problem",
                    "Challenge assumptions and risks",
                    "Set goals and success measures",
                    "Prioritise the next product move",
                    "Pulse check-ins and competitor context",
                    "Connect direction to AI build tools",
                    "3 projects",
                    "25 AI chats / day",
                    "Top-up credits anytime",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-brand-700 mt-0.5 shrink-0" />
                      <span className="text-brand-800 text-sm font-medium">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleGetStarted}
                  className="mt-8 w-full h-12 bg-brand-900 hover:bg-brand-800 text-brand-50 rounded-full font-bold text-sm transition-all duration-300"
                >
                  Get started free
                </button>
              </div>

              {/* Freelance Jobs */}
              <div className="md:col-span-1 bg-brand-800/40 border border-brand-700 rounded-3xl p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-3.5 h-3.5 text-brand-300" />
                  <p className="text-xs font-bold text-brand-300 uppercase tracking-widest">Freelance Jobs</p>
                </div>
                <div className="mb-1">
                  <span className="text-5xl font-bold text-brand-50">Top-up</span>
                </div>
                <p className="text-brand-500 text-sm mb-8">Bring Pabs in for a specific product decision, investigation, or design review. Pabs does the job, delivers the evidence, and you move forward.</p>

                <ul className="space-y-3 flex-grow">
                  {[
                    "Buy credits once, use anytime",
                    "Credits never expire",
                    "Competitor scans, product decisions, pulses",
                    "Design Authority reviews and checks",
                    "Light jobs cost less, heavy work costs more",
                    "Same pool as your monthly credits",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-brand-400 mt-0.5 shrink-0" />
                      <span className="text-brand-300 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleGetStarted}
                  className="mt-8 w-full h-12 bg-brand-700 hover:bg-brand-600 text-brand-50 rounded-full font-bold text-sm transition-all duration-300"
                >
                  Start free, top up later
                </button>
              </div>

              {/* Full-time Jobs */}
              <div className="md:col-span-1 bg-brand-800/40 border border-brand-700 rounded-3xl p-8 flex flex-col">
                <p className="text-xs font-bold text-brand-400 uppercase tracking-widest mb-4">Full-time Jobs</p>
                <div className="mb-1">
                  <span className="text-5xl font-bold text-brand-50">$19.99</span>
                  <span className="text-brand-500 text-sm ml-1">/month</span>
                </div>
                <p className="text-brand-500 text-sm mb-8">For a live product with decisions arriving continuously. Pabs stays on the product, keeps context, challenges drift, and surfaces what needs your call.</p>

                <ul className="space-y-3 flex-grow">
                  {[
                    `${proMonthlyCredits} AI credits / month`,
                    "10 projects + extra slots",
                    "Unlimited product strategy chat",
                    "Product and Design Authority jobs",
                    "Enriched competitor data",
                    "Branded public shares",
                    "Full access to 4 specialist Pabs agents",
                    "Product feedback integrations",
                    "Priority AI processing",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-brand-400 mt-0.5 shrink-0" />
                      <span className="text-brand-300 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleGetStarted}
                  className="mt-8 w-full h-12 bg-brand-700 hover:bg-brand-600 text-brand-50 rounded-full font-bold text-sm transition-all duration-300"
                >
                  Start free, upgrade when ready
                </button>
              </div>

            </div>
          </div>
        </section>

        {/* Cost comparison - old world vs Pabs */}
        <section className="py-20 border-t border-brand-800">
          <div className="container mx-auto max-w-5xl px-4 mb-16">
            <h2 className="text-3xl md:text-5xl font-medium text-brand-50 mb-6">
              Product decisions are already costing you.
            </h2>
            <p className="text-lg text-brand-200 font-light max-w-3xl">
              The cost is just hidden. You pay for it with founder time, expensive outside help,
              or a larger product and design team. When nobody owns the intent and the standards,
              you pay again in weak priorities, inconsistent experiences, and avoidable rework.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 mt-10 gap-5">
              {[
                {
                  label: "Do it yourself",
                  price: "5–10 hrs / week",
                  detail: "That is 260–520 founder hours a year spent framing, prioritising, researching, and keeping direction intact."
                },
                {
                  label: "Bring in a consultant",
                  price: "$150 / hour",
                  detail: "Useful expertise, but even one day a month costs $14,400 a year, and the context leaves when they do."
                },
                {
                  label: "Hire a product manager",
                  price: "$100k+ / year",
                  detail: "Before benefits, recruitment, onboarding, and the commitment of adding another full-time salary."
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="p-6 md:p-8 border border-brand-700 rounded-3xl bg-brand-800/30"
                >
                  <p className="text-xs font-bold text-brand-200 uppercase tracking-widest mb-3">
                    {item.label}
                  </p>
                  <p className="text-2xl md:text-3xl font-semibold text-brand-50 mb-3">
                    {item.price}
                  </p>
                  <p className="text-sm text-brand-200 leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-5 p-6 md:p-8 bg-brand-50 rounded-3xl">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div className="max-w-2xl">
                  <p className="text-xs font-bold text-brand-700 uppercase tracking-widest mb-3">
                    The third option: Pabs
                  </p>
                  <p className="text-3xl md:text-4xl font-semibold text-brand-900 mb-4">
                    Product Authority, on demand.
                  </p>
                  <p className="text-base md:text-lg text-brand-700 leading-relaxed">
                    Call Pabs in for the outcome you need: frame the problem, challenge an assumption,
                    prioritise the next move, investigate the market, or review a concept or build against its design standards.
                    The job gets done. You make the call.
                  </p>
                </div>
                <div className="md:text-right shrink-0 border-t md:border-t-0 md:border-l border-brand-300 pt-5 md:pt-0 md:pl-8">
                  <p className="text-sm font-bold text-brand-700 uppercase tracking-widest mb-2">
                    Start free
                  </p>
                  <p className="text-2xl font-semibold text-brand-900">Pay per job</p>
                  <p className="text-sm text-brand-700 mt-2">Or keep Pabs running for $19.99/month.</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 pt-6 border-t border-brand-300">
                {[
                  "A defined product problem",
                  "A prioritised next move",
                  "A challenged assumption",
                  "A reviewed design or build",
                ].map((outcome) => (
                  <div key={outcome} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-brand-700 shrink-0" />
                    <span className="text-sm font-medium text-brand-800">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

            <div className="flex gap-5 overflow-x-auto pb-6 px-4 md:px-8 snap-x snap-mandatory scrollbar-hide">
              {[
                {
                  job: "Problem Definition",
                  old: "The founder starts building from an idea that has never been properly framed.",
                  now: "Pabs defines the customer, problem, assumptions, and evidence needed before the build runs away."
                },
                {
                  job: "Product Prioritisation",
                  old: "The loudest idea or easiest feature becomes the roadmap.",
                  now: "Pabs makes trade-offs visible and grounds the next move in customer and business value."
                },
                {
                  job: "Assumption Challenge",
                  old: "Nobody is in the room to push back, so founder conviction quietly becomes product truth.",
                  now: "Pabs challenges the audience, value, scope, and evidence before a weak call becomes expensive code."
                },
                {
                  job: "Success Measures",
                  old: "Shipping becomes the measure of progress, even when the product is not proving anything.",
                  now: "Pabs sets explicit outcomes and keeps decisions tied to what success is supposed to mean."
                },
                {
                  job: "Product Pulse",
                  old: "The strategic review becomes a monthly meeting, then a quarterly one, then disappears.",
                  now: "Pabs checks the product on a schedule and surfaces the one decision that needs attention."
                },
                {
                  job: "Competitive Context",
                  old: "Market context is researched once and goes stale while the product keeps moving.",
                  now: "Pabs brings relevant competitive evidence back into the decisions being made now."
                },
                {
                  job: "Build Continuity",
                  old: "Product direction lives in a founder's head while AI builders optimise the immediate request.",
                  now: "Pabs connects live product context to the build and brings new learnings back into the plan."
                },
                {
                  job: "The Boundary",
                  old: "Product management and project management get collapsed into one overloaded role.",
                  now: "Pabs manages the product decisions. Linear, Jira, and your team still manage tickets, sprints, and delivery."
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="snap-start shrink-0 w-72 md:w-80 bg-brand-800/40 border border-brand-700 rounded-3xl p-8 flex flex-col justify-between"
                  style={{ minHeight: '360px' }}
                >
                  <div>
                    <span className="text-8xl font-bold text-brand-600 leading-none select-none">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <p className="text-xs font-bold text-brand-400 uppercase tracking-widest mt-6 mb-5">
                      {item.job}
                    </p>
                    <p className="text-xs font-bold text-brand-700 uppercase tracking-widest mb-2">Before</p>
                    <p className="text-brand-500 text-sm leading-relaxed">
                      {item.old}
                    </p>
                  </div>
                  <div className="mt-8 pt-6 border-t border-brand-700">
                    <p className="text-xs font-bold text-brand-300 uppercase tracking-widest mb-2">Now</p>
                    <p className="text-brand-100 text-sm leading-relaxed font-medium">
                      {item.now}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          <p className="text-brand-300 font-medium mt-10 text-center px-4">
            All of it. Running continuously. Start free.
          </p>
        </section>

        {/* FAQ */}
        <section className="py-20 px-4 border-t border-brand-800">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl md:text-5xl font-medium text-brand-50 mb-16">
              The honest answers.
            </h2>

            <div className="space-y-12">
              {[
                {
                  q: "I already use ChatGPT or Claude for this.",
                  a: "Keep using them. AI clients are excellent execution environments. Pabs adds the durable product context, Design Authority jobs, evidence, and decision history that a general assistant does not own. Through MCP, that authority can travel into the tools where your team already builds."
                },
                {
                  q: "I'm too far into the build for strategy.",
                  a: "That's exactly the moment Pabs is built for. Strategy Pulse is designed to catch drift mid-build. Run your current product through it, see if any decision shows up you'd revisit. Takes 15 minutes. Worst case you waste 15 minutes."
                },
                {
                  q: "Is it free? What's the catch?",
                  a: `Free is not a trial. It includes 3 projects, 25 chats a day, MCP integration, and optional credit top-ups. For $19.99 a month, Full-Time Jobs adds ${proMonthlyCredits} credits, unlimited chat, and access to all 4 specialist Pabs agents. Top-up credits never expire.`
                },
                {
                  q: "Why pay $19.99 when free already covers strategy generation?",
                  a: "Free helps you frame the product and reach the first clear decisions. Full-Time Jobs is for continuous Product and Design Authority work: new evidence, scheduled checks, design reviews, and decisions that need to stay connected as the product changes."
                },
              ].map((item, idx) => (
                <div key={idx} className="border-t border-brand-800 pt-10">
                  <h3 className="text-xl font-medium text-brand-50 mb-4">
                    "{item.q}"
                  </h3>
                  <p className="text-brand-400 leading-relaxed font-light">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20 px-4 border-t border-brand-800">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-2xl md:text-4xl font-medium text-brand-50 mb-6">
              Pabs keeps product intent and design quality under control. You make the calls.
            </h2>
            <p className="text-lg text-brand-400 leading-relaxed mb-10 font-light">
              Pabs defines, challenges, prioritises, and reviews the product. You keep ownership of
              the judgment. Your agents and delivery tools keep ownership of execution.
            </p>
            <button
              onClick={handleGetStarted}
              className="h-16 px-10 bg-brand-50 text-brand-900 hover:bg-brand-200 text-lg rounded-full font-bold shadow-xl transition-all duration-300"
            >
              Get started for free
            </button>
            <p className="text-sm text-brand-600 mt-4">
              Early adopters will always be recognised and rewarded for helping us build Pabs.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
